import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { category: "", name: "", description: "", price: 10.0, image_url: "", available: true };

/**
 * Per-restaurant menu management section (embedded inside AdminRestaurantDetailPage).
 */
export default function MenuSection({ restaurantId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [filterCategory, setFilterCategory] = useState("all");
    const [search, setSearch] = useState("");

    const load = useCallback(async () => {
        if (!restaurantId) return;
        try {
            setLoading(true);
            const { data } = await api.get(`/restaurants/${restaurantId}/menu`);
            setItems(data || []);
        } catch (error) {
            console.warn("[admin-menu] load failed", error?.message);
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        load();
    }, [load]);

    const categories = useMemo(() => Array.from(new Set(items.map(i => i.category))).sort(), [items]);
    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter(it => {
            if (filterCategory !== "all" && it.category !== filterCategory) return false;
            if (q && !`${it.name} ${it.description}`.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [items, filterCategory, search]);

    const startCreate = () => {
        setEditing(null);
        setForm({ ...EMPTY, category: categories[0] || "Mains" });
        setOpen(true);
    };
    const startEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };
    const remove = async (it) => {
        if (!window.confirm(`Delete ${it.name}?`)) return;
        try {
            await api.delete(`/admin/menu-items/${it.id}`);
            toast.success("Deleted");
            load();
        } catch (error) {
            console.warn("[admin-menu] delete failed", error?.message);
            toast.error("Failed");
        }
    };
    const save = async () => {
        setSaving(true);
        try {
            if (editing) await api.patch(`/admin/menu-items/${editing.id}`, form);
            else await api.post("/admin/menu-items", { ...form, restaurant_id: restaurantId });
            toast.success("Saved");
            setOpen(false);
            load();
        } catch (error) {
            console.warn("[admin-menu] save failed", error?.message);
            toast.error(error?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

    if (loading) return <Skeleton className="h-72" />;

    return (
        <div>
            <Card className="mb-4">
                <CardContent className="p-4 flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[200px]">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Search</Label>
                        <div className="relative mt-1">
                            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search dishes…" className="pl-9" data-testid="menu-section-search" />
                        </div>
                    </div>
                    <div className="w-44">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
                        <Select value={filterCategory} onValueChange={setFilterCategory}>
                            <SelectTrigger className="mt-1" data-testid="menu-section-category-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All categories</SelectItem>
                                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={startCreate} data-testid="admin-menu-create">
                        <Plus className="h-4 w-4 mr-1" /> Add item
                    </Button>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-0 overflow-x-auto">
                    <Table data-testid="admin-menu-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead>Price</TableHead>
                                <TableHead>Available</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {visible.length === 0 ? (
                                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No menu items</TableCell></TableRow>
                            ) : (
                                visible.map(it => (
                                    <TableRow key={it.id}>
                                        <TableCell><div className="font-medium">{it.name}</div><div className="text-xs text-muted-foreground line-clamp-1">{it.description}</div></TableCell>
                                        <TableCell>{it.category}</TableCell>
                                        <TableCell>${it.price.toFixed(2)}</TableCell>
                                        <TableCell>{it.available ? "Yes" : "No"}</TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => startEdit(it)}><Pencil className="h-4 w-4" /></Button>
                                            <Button variant="ghost" size="sm" onClick={() => remove(it)} className="text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4" /></Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
                    <SheetHeader><SheetTitle>{editing ? "Edit" : "New"} menu item</SheetTitle></SheetHeader>
                    <div className="space-y-3 mt-4">
                        <FormRow label="Category"><Input value={form.category} onChange={(e) => update("category", e.target.value)} placeholder="Mains" data-testid="admin-menu-form-category" /></FormRow>
                        <FormRow label="Name"><Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="admin-menu-form-name" /></FormRow>
                        <FormRow label="Description"><Textarea value={form.description} onChange={(e) => update("description", e.target.value)} /></FormRow>
                        <div className="grid grid-cols-2 gap-3">
                            <FormRow label="Price"><Input type="number" step="0.50" value={form.price} onChange={(e) => update("price", parseFloat(e.target.value))} /></FormRow>
                            <FormRow label="Available">
                                <div className="flex items-center gap-2 h-9">
                                    <Switch checked={form.available} onCheckedChange={(v) => update("available", v)} />
                                    <span className="text-sm">{form.available ? "Yes" : "No"}</span>
                                </div>
                            </FormRow>
                        </div>
                        <FormRow label="Image URL"><Input value={form.image_url} onChange={(e) => update("image_url", e.target.value)} /></FormRow>
                    </div>
                    <SheetFooter className="mt-6">
                        <Button onClick={save} disabled={saving} data-testid="admin-menu-save">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>
        </div>
    );
}

function FormRow({ label, children }) {
    return <div className="grid gap-1"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>;
}
